import React from 'react';
import './DataStructures.css'; // Re-using the same CSS for now

const Http = () => {
  return (
    <div className="data-structure-section">
      <h2>HTTP Concepts</h2>
      <p>
        A strong understanding of HTTP is essential for any senior engineer, especially when dealing with APIs and web services.
      </p>

      <div className="topic-section">
        <h3>HTTP Request Methods</h3>
        <p>HTTP defines a set of request methods to indicate the desired action to be performed for a given resource.</p>
        <ul>
          <li><strong>GET:</strong> Requests a representation of the specified resource. Requests using GET should only retrieve data.</li>
          <li><strong>POST:</strong> Submits an entity to the specified resource, often causing a change in state or side effects on the server. Used to create new resources.</li>
          <li><strong>PUT:</strong> Replaces all current representations of the target resource with the request payload. It is idempotent, meaning multiple identical requests should have the same effect as a single one.</li>
          <li><strong>DELETE:</strong> Deletes the specified resource.</li>
          <li><strong>PATCH:</strong> Applies partial modifications to a resource.</li>
          <li><strong>HEAD:</strong> Asks for a response identical to a GET request, but without the response body.</li>
          <li><strong>OPTIONS:</strong> Describes the communication options for the target resource.</li>
        </ul>
      </div>

       <div className="topic-section">
        <h3>Idempotency</h3>
        <p>This is a very common interview question. An HTTP method is <strong>idempotent</strong> if an identical request can be made once or several times in a row with the same effect.
        </p>
        <ul>
          <li><strong>Idempotent:</strong> GET, PUT, DELETE, HEAD, OPTIONS.</li>
          <li><strong>Not Idempotent:</strong> POST, PATCH (PATCH can be idempotent but isn't guaranteed to be).</li>
        </ul>
        <p>For example, creating an order should be done with POST. If you sent the same POST request twice, you would create two orders. Deleting an order with DELETE is idempotent; if you send the request twice, the order is still deleted after the first request and the system state doesn't change on the second request (you might get a '404 Not Found' response, but the resource state is the same).</p>
      </div>

      <div className="topic-section">
        <h3>Common Status Codes</h3>
        <p>You should be familiar with the different categories of HTTP status codes.</p>
        <ul>
            <li><strong>1xx (Informational):</strong> Request received, continuing process.</li>
            <li><strong>2xx (Successful):</strong> The request was successfully received, understood, and accepted.
                <ul>
                    <li><strong>200 OK:</strong> Standard response for successful HTTP requests.</li>
                    <li><strong>201 Created:</strong> The request has been fulfilled, resulting in the creation of a new resource.</li>
                    <li><strong>204 No Content:</strong> The server successfully processed the request and is not returning any content.</li>
                </ul>
            </li>
            <li><strong>3xx (Redirection):</strong> Further action needs to be taken in order to complete the request.</li>
            <li><strong>4xx (Client Error):</strong> The request contains bad syntax or cannot be fulfilled.
                <ul>
                    <li><strong>400 Bad Request:</strong> The server cannot or will not process the request due to something that is perceived to be a client error.</li>
                    <li><strong>401 Unauthorized:</strong> Authentication is required and has failed or has not yet been provided.</li>
                    <li><strong>403 Forbidden:</strong> The server understood the request but refuses to authorize it.</li>
                    <li><strong>404 Not Found:</strong> The requested resource could not be found.</li>
                </ul>
            </li>
            <li><strong>5xx (Server Error):</strong> The server failed to fulfill an apparently valid request.
                 <ul>
                    <li><strong>500 Internal Server Error:</strong> A generic error message, given when an unexpected condition was encountered.</li>
                    <li><strong>503 Service Unavailable:</strong> The server is not ready to handle the request. Common causes are a server that is down for maintenance or that is overloaded.</li>
                </ul>
            </li>
        </ul>
      </div>
    </div>
  );
};

export default Http;